import { injectable } from 'inversify';
import Mime from 'mime/lite';
import Papa from 'papaparse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../../canvas/EditorSession';
import { ContentPayload } from '../../canvas/injection/ContentPayload';
import { ContentProcessor } from '../../canvas/injection/ContentProcessor';
import { DeqoData } from '../../canvas/injection/DeqoData';
import { resolve } from '../../Container';
import { PopupManager } from '../../popups/PopupManager';
import { TabularData, TabularImportParams } from './TabularImportModel';
import { tabularImportPopup } from './TabularImportPopup';

@injectable()
export class CsvDataProcessor implements ContentProcessor {

    @resolve(CommandDispatcher) dispatch!: CommandDispatcher;
    @resolve(EditorSession) session!: EditorSession;
    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(PopupManager) popups!: PopupManager;

    public readonly supportedTypes = Object.freeze([
        Mime.getType('csv')
    ]);

    public async process(payload: ContentPayload): Promise<boolean | ContentPayload> {

        // Try read text
        const textData = await payload.getDataAsText();
        if (!textData) return false;

        const parseResult = Papa.parse(textData, { skipEmptyLines: true });
        if (!this.validate(parseResult)) return false;

        const mimeType = payload.type;
        const { method } = payload.metadata;
        const tuples = await this.invokePopup(parseResult.data as TabularData, { method, mimeType });

        if (tuples && tuples.length) {
            const ddt = DeqoData.wrap(tuples);
            return ContentPayload.derive(payload, 'x/deqo-tuple', ddt);
        }

        return Promise.resolve(true);
    }

    private async invokePopup(data: TabularData, initialParams: TabularImportParams) {
        const pr = await this.popups.invoke(tabularImportPopup(data, initialParams));
        if (!pr.data) return [];
        const { params, strategy } = pr.data;
        return strategy.apply(data, params);
    }

    private validate(result: Papa.ParseResult<any>) {
        if (result.data.length < 2) return false;
        const first = result.data[0];
        if (!('length' in first)) return false;
        return !result.data.some(x => x.length !== first.length);
    }
}
